import { useState } from 'react';
import { useTheme } from '@/lib/theme';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DatePicker } from '@/components/ui/date-picker';
import { toast } from 'sonner';
import {
  Sun,
  Moon,
  Check,
  AlertTriangle,
  Info,
  XCircle,
  Search,
  Mail,
  Loader2,
  TrendingUp,
  Inbox,
} from 'lucide-react';

const COLOR_TOKENS: { group: string; tokens: { name: string; varName: string }[] }[] = [
  {
    group: 'Brand',
    tokens: [
      { name: 'brand', varName: '--color-brand-navy' },
      { name: 'brand.foreground', varName: '--color-brand-navy-foreground' },
    ],
  },
  {
    group: 'Accent',
    tokens: [
      { name: 'accent', varName: '--color-accent' },
      { name: 'accent.hover', varName: '--color-accent-hover' },
      { name: 'accent.subtle', varName: '--color-accent-subtle' },
    ],
  },
  {
    group: 'Surface',
    tokens: [
      { name: 'surface.page', varName: '--color-surface-page' },
      { name: 'surface.card', varName: '--color-surface-card' },
      { name: 'surface.raised', varName: '--color-surface-raised' },
      { name: 'surface.sunken', varName: '--color-surface-sunken' },
    ],
  },
  {
    group: 'Text',
    tokens: [
      { name: 'text.primary', varName: '--color-text-primary' },
      { name: 'text.secondary', varName: '--color-text-secondary' },
      { name: 'text.tertiary', varName: '--color-text-tertiary' },
      { name: 'text.disabled', varName: '--color-text-disabled' },
    ],
  },
  {
    group: 'Border',
    tokens: [
      { name: 'border.subtle', varName: '--color-border-subtle' },
      { name: 'border.strong', varName: '--color-border-strong' },
    ],
  },
  {
    group: 'Semantic',
    tokens: [
      { name: 'success', varName: '--color-success' },
      { name: 'success.subtle', varName: '--color-success-subtle' },
      { name: 'warning', varName: '--color-warning' },
      { name: 'warning.subtle', varName: '--color-warning-subtle' },
      { name: 'danger', varName: '--color-danger' },
      { name: 'danger.subtle', varName: '--color-danger-subtle' },
      { name: 'info', varName: '--color-info' },
      { name: 'info.subtle', varName: '--color-info-subtle' },
    ],
  },
];

function Section({
  id,
  title,
  children,
}: {
  id: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="border-t border-stroke-subtle py-12">
      <h2 className="font-display text-heading-1 text-ink-primary mb-6 tracking-tight">
        {title}
      </h2>
      <div className="space-y-6">{children}</div>
    </section>
  );
}

function Swatch({ name, varName }: { name: string; varName: string }) {
  return (
    <div className="rounded-token-md overflow-hidden border border-stroke-subtle bg-surface-card shadow-token-sm">
      <div
        className="h-20 w-full"
        style={{ background: `hsl(var(${varName}))` }}
      />
      <div className="p-3 space-y-1">
        <div className="font-mono text-body-sm text-ink-primary">{name}</div>
        <div className="font-mono text-caption text-ink-tertiary">{varName}</div>
      </div>
    </div>
  );
}

export default function StyleGuide() {
  const { theme, toggle } = useTheme();
  const [loading, setLoading] = useState(false);

  return (
    <div className="min-h-screen bg-surface-page text-ink-primary">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-stroke-subtle bg-surface-page/80 backdrop-blur">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-6 py-4">
          <div>
            <div className="font-display text-heading-3 tracking-tight">
              RLevents Style Guide
            </div>
            <div className="text-body-sm text-ink-secondary">
              Slice D28 — Phase 1A design tokens
            </div>
          </div>
          <button
            onClick={toggle}
            className="inline-flex items-center gap-2 rounded-token-md border border-stroke-strong bg-surface-card px-4 py-2 text-body-sm font-medium text-ink-primary hover:bg-surface-raised transition-colors duration-fast ease-out"
          >
            {theme === 'light' ? (
              <>
                <Moon className="h-4 w-4" strokeWidth={1.5} /> Dark
              </>
            ) : (
              <>
                <Sun className="h-4 w-4" strokeWidth={1.5} /> Light
              </>
            )}
          </button>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 pb-24">
        {/* Typography */}
        <Section id="typography" title="Typography">
          <div className="space-y-3">
            <p className="font-display text-display-xl">Bears Blue 17u</p>
            <p className="font-display text-display-lg">Bears Blue 17u</p>
            <p className="font-display text-display-md">Bears Blue 17u</p>
            <p className="font-display text-display-sm">Bears Blue 17u</p>
          </div>
          <div className="space-y-2 pt-6 border-t border-stroke-subtle">
            <p className="text-heading-1">Heading 1 — semibold</p>
            <p className="text-heading-2">Heading 2 — semibold</p>
            <p className="text-heading-3">Heading 3 — semibold</p>
            <p className="text-heading-4">Heading 4 — semibold</p>
          </div>
          <div className="space-y-2 pt-6 border-t border-stroke-subtle">
            <p className="text-body-lg">Body large — 17px. The quick brown fox.</p>
            <p className="text-body-md">Body — 15px. The quick brown fox jumps.</p>
            <p className="text-body-sm text-ink-secondary">
              Body small — 13px. Secondary tone.
            </p>
            <p className="text-caption uppercase text-ink-tertiary">
              Caption — 12px tracked
            </p>
          </div>
          <div className="pt-6 border-t border-stroke-subtle">
            <p className="font-mono tabular-nums text-body-md text-ink-primary">
              $1,250.00 &nbsp;·&nbsp; 14:30:00 &nbsp;·&nbsp; TEAM-001
            </p>
          </div>
        </Section>

        {/* Colors */}
        <Section id="colors" title="Color Tokens">
          {COLOR_TOKENS.map((group) => (
            <div key={group.group}>
              <h3 className="text-heading-4 text-ink-secondary mb-3">
                {group.group}
              </h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                {group.tokens.map((t) => (
                  <Swatch key={t.varName} {...t} />
                ))}
              </div>
            </div>
          ))}
        </Section>

        {/* Buttons */}
        <Section id="buttons" title="Buttons">
          <div className="space-y-4">
            <div className="flex flex-wrap gap-3">
              <Button>Primary</Button>
              <Button variant="secondary">Secondary</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="destructive">Destructive</Button>
              <Button variant="link">Link</Button>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button size="sm">Small</Button>
              <Button size="default">Medium</Button>
              <Button size="lg">Large</Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button disabled>Disabled</Button>
              <Button>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} />
                Loading
              </Button>
            </div>
            <div className="flex flex-wrap gap-3">
              <button className="rounded-token-md bg-cyan px-4 py-2 text-body-sm font-medium text-cyan-foreground hover:bg-cyan-hover transition-colors duration-fast ease-out">
                Cyan accent (token)
              </button>
              <button className="rounded-token-md bg-brand px-4 py-2 text-body-sm font-medium text-brand-foreground transition-colors duration-fast ease-out">
                Brand navy (token)
              </button>
            </div>
          </div>
        </Section>

        {/* Form fields */}
        <Section id="forms" title="Form Fields">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <Label>Default input</Label>
              <Input placeholder="Type something" />
              <Input defaultValue="With value" />
              <div className="relative">
                <Search
                  className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-tertiary"
                  strokeWidth={1.5}
                />
                <Input className="pl-9" placeholder="Search" />
              </div>
              <div className="relative">
                <Input className="pr-9" placeholder="Email" />
                <Mail
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-tertiary"
                  strokeWidth={1.5}
                />
              </div>
              <Input disabled placeholder="Disabled" />
              <Input
                className="border-status-danger focus-visible:ring-status-danger"
                defaultValue="invalid@"
              />
            </div>
            <div className="space-y-3">
              <Label>Textarea</Label>
              <Textarea placeholder="Tell us more..." rows={4} />
              <Label>Select</Label>
              <Select>
                <SelectTrigger>
                  <SelectValue placeholder="Pick one" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="a">Option A</SelectItem>
                  <SelectItem value="b">Option B</SelectItem>
                  <SelectItem value="c">Option C</SelectItem>
                </SelectContent>
              </Select>
              <Label>Date picker</Label>
              <DatePicker />
            </div>
          </div>
          <div className="grid md:grid-cols-3 gap-6 pt-6 border-t border-stroke-subtle">
            <div className="space-y-3">
              <Label>Checkboxes</Label>
              <div className="flex items-center gap-2">
                <Checkbox id="cb1" /> <Label htmlFor="cb1">Unchecked</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="cb2" defaultChecked /> <Label htmlFor="cb2">Checked</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="cb3" disabled /> <Label htmlFor="cb3">Disabled</Label>
              </div>
            </div>
            <div className="space-y-3">
              <Label>Radio</Label>
              <RadioGroup defaultValue="r2">
                {['r1', 'r2', 'r3'].map((v, i) => (
                  <div key={v} className="flex items-center gap-2">
                    <RadioGroupItem value={v} id={v} />
                    <Label htmlFor={v}>Option {i + 1}</Label>
                  </div>
                ))}
              </RadioGroup>
            </div>
            <div className="space-y-3">
              <Label>Switch</Label>
              <div className="flex items-center gap-2">
                <Switch id="sw1" /> <Label htmlFor="sw1">Off</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="sw2" defaultChecked /> <Label htmlFor="sw2">On</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="sw3" disabled /> <Label htmlFor="sw3">Disabled</Label>
              </div>
            </div>
          </div>
        </Section>

        {/* Badges */}
        <Section id="badges" title="Badges">
          <div className="flex flex-wrap gap-3">
            <Badge>Default</Badge>
            <Badge variant="secondary">Secondary</Badge>
            <Badge variant="outline">Outline</Badge>
            <Badge variant="destructive">Destructive</Badge>
          </div>
          <div className="flex flex-wrap gap-3">
            <span className="inline-flex items-center gap-1 rounded-token-full bg-status-success-subtle px-2.5 py-1 text-caption font-medium text-status-success">
              <Check className="h-3 w-3" strokeWidth={1.5} /> Success
            </span>
            <span className="inline-flex items-center gap-1 rounded-token-full bg-status-warning-subtle px-2.5 py-1 text-caption font-medium text-status-warning">
              <AlertTriangle className="h-3 w-3" strokeWidth={1.5} /> Warning
            </span>
            <span className="inline-flex items-center gap-1 rounded-token-full bg-status-danger-subtle px-2.5 py-1 text-caption font-medium text-status-danger">
              <XCircle className="h-3 w-3" strokeWidth={1.5} /> Danger
            </span>
            <span className="inline-flex items-center gap-1 rounded-token-full bg-status-info-subtle px-2.5 py-1 text-caption font-medium text-status-info">
              <Info className="h-3 w-3" strokeWidth={1.5} /> Info
            </span>
          </div>
        </Section>

        {/* Cards */}
        <Section id="cards" title="Cards">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card p-5 shadow-token-sm">
              <p className="text-body-md">Plain card</p>
            </div>
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card shadow-token-md">
              <div className="flex items-center justify-between p-5 border-b border-stroke-subtle">
                <h4 className="text-heading-4">Card title</h4>
                <Button size="sm" variant="ghost">
                  Action
                </Button>
              </div>
              <div className="p-5 text-body-md text-ink-secondary">
                Body content goes here.
              </div>
              <div className="p-4 border-t border-stroke-subtle bg-surface-sunken text-body-sm text-ink-tertiary">
                Footer
              </div>
            </div>
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card p-5 shadow-token-sm">
              <div className="flex items-center justify-between">
                <span className="text-caption uppercase text-ink-tertiary">
                  Revenue
                </span>
                <TrendingUp className="h-4 w-4 text-status-success" strokeWidth={1.5} />
              </div>
              <div className="mt-2 font-display text-display-sm tabular-nums">
                $48,210
              </div>
              <div className="text-body-sm text-status-success tabular-nums">
                +12.4% vs last week
              </div>
            </div>
          </div>
        </Section>

        {/* Empty state */}
        <Section id="empty" title="Empty State">
          <div className="rounded-token-lg border border-dashed border-stroke-strong bg-surface-card p-10 text-center">
            <div className="mx-auto h-16 w-16 rounded-token-full bg-surface-raised flex items-center justify-center">
              <Inbox className="h-7 w-7 text-ink-tertiary" strokeWidth={1.5} />
            </div>
            <h3 className="mt-4 text-heading-3">Nothing here yet</h3>
            <p className="mt-1 text-body-md text-ink-secondary">
              When you add a team, it will appear in this list.
            </p>
            <Button className="mt-4">Add team</Button>
          </div>
        </Section>

        {/* Skeletons */}
        <Section id="skeletons" title="Loading Skeletons">
          <div className="grid md:grid-cols-3 gap-4">
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card p-5 space-y-3">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </div>
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card p-5 space-y-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-token-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-3 w-2/3" />
                    <Skeleton className="h-3 w-1/3" />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-token-lg border border-stroke-subtle bg-surface-card p-5 space-y-3">
              <Skeleton className="h-3 w-1/4" />
              <Skeleton className="h-10 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          </div>
        </Section>

        {/* Toasts */}
        <Section id="toasts" title="Toasts">
          <div className="flex flex-wrap gap-3">
            <Button variant="outline" onClick={() => toast.success('Saved successfully')}>
              Success
            </Button>
            <Button variant="outline" onClick={() => toast.warning('Heads up')}>
              Warning
            </Button>
            <Button variant="outline" onClick={() => toast.error('Something went wrong')}>
              Danger
            </Button>
            <Button variant="outline" onClick={() => toast.info('New update available')}>
              Info
            </Button>
          </div>
        </Section>

        {/* Motion */}
        <Section id="motion" title="Motion">
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { d: 'duration-fast', label: '150ms · fast' },
              { d: 'duration-200', label: '200ms · default' },
              { d: 'duration-slow', label: '300ms · slow' },
            ].map((m) => (
              <button
                key={m.d}
                onClick={() => {
                  setLoading(true);
                  setTimeout(() => setLoading(false), 400);
                }}
                className={`group rounded-token-lg border border-stroke-subtle bg-surface-card p-6 text-left transition-all ${m.d} ease-out hover:-translate-y-1 hover:shadow-token-lg hover:border-cyan motion-reduce:transform-none motion-reduce:transition-none`}
              >
                <div className="font-mono text-caption text-ink-tertiary">
                  {m.label}
                </div>
                <div className="mt-2 text-heading-3">Hover me</div>
              </button>
            ))}
          </div>
          {loading && (
            <p className="text-body-sm text-ink-tertiary">animating…</p>
          )}
        </Section>
      </main>
    </div>
  );
}
